/**
 * WhatsApp Cloud API media constraints.
 * Source: Meta Cloud API docs — https://developers.facebook.com/docs/whatsapp/cloud-api/reference/media
 *
 * Note on document size: Meta allows up to 100 MB, but our wabaserver caps the
 * JSON body at 50 MB and we send files as base64 (~33% inflation), so the
 * effective ceiling is ~37 MB. We cap at 35 MB to keep a safety margin.
 */

const MB = 1024 * 1024;

export const MEDIA_KIND = {
  IMAGE: "image",
  VIDEO: "video",
  AUDIO: "audio",
  DOCUMENT: "document",
};

export const MEDIA_LIMITS = {
  [MEDIA_KIND.IMAGE]: {
    label: "Photo",
    maxBytes: 5 * MB,
    maxLabel: "5 MB",
    accept: "image/jpeg,image/jpg,image/png",
    extensions: ["jpg", "jpeg", "png"],
    mimePrefix: "image/",
  },
  [MEDIA_KIND.VIDEO]: {
    label: "Video",
    maxBytes: 16 * MB,
    maxLabel: "16 MB",
    accept: "video/mp4,video/3gpp",
    extensions: ["mp4", "3gp"],
    mimePrefix: "video/",
  },
  [MEDIA_KIND.AUDIO]: {
    label: "Audio",
    maxBytes: 16 * MB,
    maxLabel: "16 MB",
    // M4A often surfaces as audio/mp4 or audio/x-m4a; accept both.
    accept:
      "audio/aac,audio/amr,audio/mpeg,audio/mp3,audio/mp4,audio/x-m4a,audio/ogg",
    extensions: ["aac", "amr", "mp3", "m4a", "ogg"],
    mimePrefix: "audio/",
  },
  [MEDIA_KIND.DOCUMENT]: {
    label: "Document",
    maxBytes: 35 * MB, // Capped by server body-parser (50 MB) + base64 overhead
    maxLabel: "35 MB",
    accept:
      "application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,text/plain",
    extensions: ["pdf", "doc", "docx", "xls", "xlsx", "ppt", "pptx", "txt"],
    mimePrefix: null, // doc types don't share a single prefix
  },
};

/**
 * Infer the WhatsApp media kind from a File. Falls back to extension when
 * the browser's MIME guess is empty (common for .m4a / .amr).
 */
export function inferMediaKind(file) {
  if (!file) return null;
  const type = (file.type || "").toLowerCase();
  if (type.startsWith("image/")) return MEDIA_KIND.IMAGE;
  if (type.startsWith("video/")) return MEDIA_KIND.VIDEO;
  if (type.startsWith("audio/")) return MEDIA_KIND.AUDIO;

  // Fallback: extension-based guess for documents and oddly-typed audio files.
  const ext = (file.name || "").split(".").pop()?.toLowerCase() || "";
  for (const [kind, cfg] of Object.entries(MEDIA_LIMITS)) {
    if (cfg.extensions.includes(ext)) return kind;
  }
  return MEDIA_KIND.DOCUMENT; // Last resort — treat as document.
}

/**
 * Validate a file against the WhatsApp limits for a given (or inferred) kind.
 * Returns { ok: true, kind } when valid, otherwise { ok: false, reason }.
 */
export function validateMediaFile(file, expectedKind = null) {
  if (!file) return { ok: false, reason: "No file selected" };

  const kind = expectedKind || inferMediaKind(file);
  const limits = MEDIA_LIMITS[kind];
  if (!limits) return { ok: false, reason: "Unsupported file type" };

  // Cross-check inferred kind matches expected when one is given (e.g. user
  // picks "Photo" but selects a PDF).
  if (expectedKind) {
    const inferred = inferMediaKind(file);
    if (inferred !== expectedKind) {
      return {
        ok: false,
        reason: `This slot accepts ${limits.label.toLowerCase()} files only`,
        kind,
      };
    }
  }

  if (file.size > limits.maxBytes) {
    return {
      ok: false,
      reason: `${limits.label} must be under ${limits.maxLabel}`,
      kind,
    };
  }

  return { ok: true, kind };
}

export function formatFileSize(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < MB) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / MB).toFixed(1)} MB`;
}
